import { useState, useEffect, useCallback } from "react";

import axios from "axios";

import { orgSessionManager } from "@domains/auth/services/orgSessionManager";
import { apiBaseUrls } from "@platform/constants/shared-constants";

// ── Types ─────────────────────────────────────────────────────────────────────

export interface PipelineRunRow {
  id: string;
  sagaId: string;
  systemId: number;
  systemAlias: string;
  organizationId: number;
  trigger: string;
  status: "queued" | "in_progress" | "completed" | "failed";
  sha: string | null;
  branch: string | null;
  createdAt: string;
  updatedAt: string;
  completedAt: string | null;
}

export interface UsePipelineRunsApiResult {
  runs: PipelineRunRow[];
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

// ── Hook ───────────────────────────────────────────────────────────────────────

/**
 * Fetches pipeline runs from the REST API and merges with WebSocket-driven
 * pipelineState updates for real-time status changes.
 *
 * The REST endpoint returns the authoritative list of runs (including "queued"
 * runs that haven't received any events yet). The WebSocket updates flow through
 * PipelineStep events and update the run status in real-time.
 */
export function usePipelineRunsApi(
  alias: string | undefined,
): UsePipelineRunsApiResult {
  const [runs, setRuns] = useState<PipelineRunRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const token = () => orgSessionManager.getToken() ?? "";

  const fetchRuns = useCallback(async () => {
    if (!alias) return;
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get<{
        runs: PipelineRunRow[];
        total: number;
      }>(`${apiBaseUrls.BACKEND}/api/v1/applications/${alias}/pipeline-runs`, {
        headers: { Authorization: `Bearer ${token()}` },
      });
      setRuns(res.data.runs);
    } catch (err) {
      if (axios.isAxiosError(err) && err.response?.status === 403) {
        setError("Organization mismatch");
      } else {
        setError("Failed to fetch pipeline runs");
      }
      setRuns([]);
    } finally {
      setLoading(false);
    }
  }, [alias]);

  useEffect(() => {
    void fetchRuns();
  }, [fetchRuns]);

  return { runs, loading, error, refetch: fetchRuns };
}

// ── Trigger label helper ──────────────────────────────────────────────────────

export function getTriggerLabel(trigger: string): string {
  const labels: Record<string, string> = {
    build: "Build",
    deploy: "Deploy",
    build_and_deploy: "Build + Deploy",
    retry_build: "Retry Build",
    redeploy: "Redeploy",
  };
  return labels[trigger] ?? trigger;
}
